import React, { useEffect, useMemo, useState } from "react";
import {
  Ban, CheckCircle2, ChevronDown, Crosshair, Search, Target, Wrench,
} from "lucide-react";
import { apiGet, unpackList } from "../../Api/Client.js";
import { resolveActiveEmployee } from "../../Shared/ScreenUtils.jsx";
import CallCard from "./SellerRadar/CallCard.jsx";
import BasicsFloor from "./SellerRadar/BasicsFloor.jsx";
import {
  FLOOR_WINDOW,
  isFloorMistakeKey,
  scoredCallsNewestFirst,
} from "./SellerRadar/basicsFloor.js";
import {
  callAverage, companyOf, formatDate, isRefused, refusalLabel, scoreColor,
} from "./SellerRadar/callMeta.js";

// RevOps V2 — person-first focus page. Leaves /revops/ alone.
//
// DO NOT REMOVE — growth formula (rungs 1–10):
//   Docs/RevOps-Growth-Order-Cluster-Skill.md
//   Docs/RevOps-Build-Checkpoints.md
// Checkpoint 1: basics floor (rung 2) + this dashboard. House pitch = later.
// No stage / band / letter-code jargon on the default view.
// Meet Recorder owns who-spoke mapping; we only display confirmed scores.
// Cost: UI reads stored SellerCallScores only — no live Echo calls from here.

const card = {
  background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14,
  padding: "16px 18px",
};
const muted = { color: "var(--c-500)" };

function PersonPicker({ people, selectedId, onSelect }) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={selectedId || ""}
        onChange={(e) => onSelect(e.target.value)}
        style={{
          appearance: "none", padding: "8px 32px 8px 14px", borderRadius: 999,
          border: "1px solid var(--c-200)", background: "var(--blue-50, rgba(80,120,255,.1))",
          color: "var(--blue-600)", fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}
      >
        {people.map((p) => (
          <option key={p.employee} value={p.employee}>
            {p.name}{p.role ? ` · ${p.role}` : ""}
          </option>
        ))}
      </select>
      <ChevronDown
        size={14}
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
        color="var(--blue-600)"
      />
    </div>
  );
}

function CallRow({ call, active, onClick }) {
  const { avg, count } = callAverage(call);
  const refused = isRefused(call);
  const color = scoreColor(avg);
  const company = companyOf(call);
  // Checkpoint 1: person · company · date — not Meet titles.
  const title = company || "No company";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      style={{
        display: "grid", gridTemplateColumns: "1fr auto", gap: 8, width: "100%", textAlign: "left",
        padding: "9px 10px 9px 12px", borderRadius: 9, cursor: "pointer",
        border: "1px solid transparent",
        borderLeft: `3px solid ${active ? "var(--blue-600)" : "transparent"}`,
        background: active ? "rgba(80,120,255,.13)" : "transparent",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, color: active ? "var(--c-900)" : "var(--c-800)",
          fontWeight: active ? 700 : 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {title}
        </div>
        <div style={{ fontSize: 10.5, ...muted, marginTop: 2 }}>
          {formatDate(call)}
        </div>
        {refused && (
          <div style={{
            marginTop: 3, fontSize: 10, color: "var(--c-600)",
            display: "inline-flex", alignItems: "center", gap: 3,
          }}>
            <Ban size={10} /> {refusalLabel(call)}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
          {refused ? "—" : avg ?? "—"}
        </div>
        <div style={{ fontSize: 9.5, ...muted }}>
          {refused ? "not scored" : `${count} scored`}
        </div>
      </div>
    </button>
  );
}

// Fills the panel column — no fixed maxHeight like V1's 380px cap.
function CallHistoryPanel({ calls, openId, onSelect }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");

  const sorted = useMemo(() => [...(calls || [])].sort((a, b) => {
    const ka = a.started_at || a.call_date || "";
    const kb = b.started_at || b.call_date || "";
    return kb.localeCompare(ka) || (b.id - a.id);
  }), [calls]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((c) => (kind === "all" || c.modality === kind)
      && (!q || `${companyOf(c)} ${c.call_label || ""} ${c.project_name || ""}`.toLowerCase().includes(q)));
  }, [sorted, query, kind]);

  return (
    <div style={{
      ...card, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0,
      height: "100%", padding: "14px 14px 10px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexShrink: 0 }}>
        <div style={{
          fontSize: 11.5, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: ".04em", color: "var(--c-600)",
        }}>
          Call history
        </div>
        <span style={{ fontSize: 10.5, ...muted }}>
          {(calls || []).length} call{(calls || []).length === 1 ? "" : "s"}
        </span>
      </div>

      <div style={{
        display: "flex", gap: 6, alignItems: "center", margin: "8px 0 6px", flexWrap: "wrap", flexShrink: 0,
      }}>
        <span style={{ position: "relative", display: "inline-flex", alignItems: "center", flex: "1 1 120px" }}>
          <Search size={11} style={{ position: "absolute", left: 8, color: "var(--c-500)", pointerEvents: "none" }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Company"
            aria-label="Search calls by company"
            style={{
              width: "100%", padding: "5px 8px 5px 24px", fontSize: 11.5, borderRadius: 7,
              border: "1px solid var(--c-200)", background: "var(--surface)", color: "var(--c-900)",
            }}
          />
        </span>
        <div style={{ display: "flex", gap: 3 }}>
          {[["all", "All"], ["call", "Calls"], ["meeting", "Meetings"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setKind(key)}
              style={{
                border: "1px solid var(--c-200)", cursor: "pointer", fontSize: 11, padding: "4px 8px",
                borderRadius: 7, fontWeight: kind === key ? 700 : 400,
                background: kind === key ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
                color: kind === key ? "var(--c-900)" : "var(--c-500)",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", margin: "0 -4px" }}>
        {visible.length === 0 && (
          <p style={{ fontSize: 12, ...muted, margin: "8px 6px" }}>
            {(calls || []).length === 0 ? "No scored calls yet." : "No calls match."}
          </p>
        )}
        {visible.map((c) => (
          <CallRow key={c.id} call={c} active={openId === c.id} onClick={() => onSelect(c.id)} />
        ))}
      </div>
    </div>
  );
}

function FocusSection({ icon, title, children }) {
  return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
        fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
        color: "var(--c-600)",
      }}>
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export default function RevOpsV2Screen({ data }) {
  const me = data?.me;
  const isManager = Boolean(me?.user?.isSuperuser);
  const myEmployee = resolveActiveEmployee(data);

  const [rubric, setRubric] = useState(null);
  const [calls, setCalls] = useState(null);
  const [roster, setRoster] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [patternsError, setPatternsError] = useState("");
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Recent call details (measures / floor_checks) for the basics standing.
  // Stored scores only — no Echo spend from this fetch.
  const [detailsById, setDetailsById] = useState({});

  useEffect(() => {
    let cancelled = false;
    apiGet("/RevOps/rubric/").then((r) => !cancelled && setRubric(r)).catch(() => {});
    apiGet("/RevOps/SellerCallScores/")
      .then((payload) => !cancelled && setCalls(unpackList(payload)))
      .catch(() => !cancelled && setCalls([]));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isManager) {
      setRoster([]);
      if (myEmployee?.id) setEmployeeId(String(myEmployee.id));
      return undefined;
    }
    let cancelled = false;
    apiGet("/RevOps/roster/")
      .then((payload) => {
        if (cancelled) return;
        const people = payload?.people || [];
        setRoster(people);
        setEmployeeId((prev) => prev || (people[0] ? String(people[0].employee_id) : null));
      })
      .catch(() => !cancelled && setRoster([]));
    return () => { cancelled = true; };
  }, [isManager, myEmployee?.id]);

  const people = useMemo(() => {
    const byEmployee = {};
    (calls || []).forEach((c) => {
      (byEmployee[c.employee] = byEmployee[c.employee] || []).push(c);
    });

    if (isManager && roster) {
      return (roster || []).map((p) => ({
        employee: p.employee_id,
        name: p.name,
        track: p.track,
        role: p.role,
        stage_label: p.stage_label,
        calls: byEmployee[p.employee_id] || [],
      }));
    }

    const fromCalls = Object.entries(byEmployee).map(([id, rows]) => ({
      employee: Number(id),
      name: rows[0].employee_name,
      track: rows[0].track,
      role: rows[0].track === "senior" ? "AE" : "SDR",
      calls: rows,
    }));
    // A rep with nothing scored yet still has a focus page — themselves.
    if (!isManager && fromCalls.length === 0 && myEmployee?.id) {
      return [{
        employee: myEmployee.id,
        name: myEmployee.display_name || myEmployee.name || "You",
        track: null,
        role: null,
        calls: [],
      }];
    }
    return fromCalls;
  }, [calls, roster, isManager, myEmployee]);

  useEffect(() => {
    if (!people.length) return;
    const stillThere = people.some((p) => String(p.employee) === String(employeeId));
    if (!stillThere) setEmployeeId(String(people[0].employee));
  }, [people, employeeId]);

  const person = people.find((p) => String(p.employee) === String(employeeId)) || people[0] || null;
  const personCalls = person?.calls || [];

  // Newest scored (non-refused, non-CRM) call = "analysis ready".
  const readyCall = useMemo(() => scoredCallsNewestFirst(personCalls)[0] || null, [personCalls]);

  // Pull measures for the last few scored calls (intranet only — already paid).
  useEffect(() => {
    const newest = scoredCallsNewestFirst(personCalls).slice(0, FLOOR_WINDOW);
    if (!newest.length) {
      setDetailsById({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      newest.map((c) => apiGet(`/RevOps/SellerCallScores/${c.id}/`)
        .then((payload) => [c.id, payload])
        .catch(() => [c.id, null])),
    ).then((pairs) => {
      if (cancelled) return;
      const next = {};
      for (const [id, payload] of pairs) {
        if (payload && !payload._failed) next[id] = payload;
      }
      setDetailsById(next);
    });
    return () => { cancelled = true; };
  }, [person?.employee, personCalls.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!employeeId) { setPatterns(null); return undefined; }
    let cancelled = false;
    setPatternsError("");
    const windowDays = person?.track === "senior" ? 30 : 7;
    const q = new URLSearchParams({ window: String(windowDays), employee: String(employeeId) });
    apiGet(`/RevOps/recurring-mistakes/?${q}`)
      .then((payload) => !cancelled && setPatterns(payload))
      .catch((err) => !cancelled && setPatternsError(err?.message || "Couldn't load patterns."));
    return () => { cancelled = true; };
  }, [employeeId, person?.track]);

  useEffect(() => {
    if (!selectedCallId) { setCallDetail(null); return undefined; }
    let cancelled = false;
    setDetailLoading(true);
    apiGet(`/RevOps/SellerCallScores/${selectedCallId}/`)
      .then((payload) => !cancelled && setCallDetail(payload))
      .catch(() => !cancelled && setCallDetail({ _failed: true }))
      .finally(() => !cancelled && setDetailLoading(false));
    return () => { cancelled = true; };
  }, [selectedCallId]);

  // Default-select the ready call when the person changes.
  useEffect(() => {
    setSelectedCallId(readyCall?.id ?? personCalls[0]?.id ?? null);
  }, [person?.employee]); // eslint-disable-line react-hooks/exhaustive-deps

  // Checkpoint 1: only basics-floor mistakes are loud. Deal skills wait.
  const floorMistakes = useMemo(() => (
    (patterns?.mistakes || []).filter((m) => isFloorMistakeKey(m.parameter_key))
  ), [patterns]);

  const drill = useMemo(() => {
    const preferred = patterns?.drill_this_week;
    if (preferred && isFloorMistakeKey(preferred)) {
      return floorMistakes.find((m) => m.parameter_key === preferred) || null;
    }
    return floorMistakes.find((m) => m.status === "chronic" || m.status === "recurring")
      || floorMistakes[0]
      || null;
  }, [patterns, floorMistakes]);

  const repeating = useMemo(() => (
    floorMistakes.filter((m) => m.status === "chronic" || m.status === "recurring")
  ), [floorMistakes]);

  const listed = personCalls.find((c) => c.id === selectedCallId) || null;
  const openCall = listed && callDetail && !callDetail._failed
    ? { ...listed, ...callDetail }
    : listed;
  const loading = !calls || (isManager && !roster);

  const openReady = () => {
    if (readyCall) setSelectedCallId(readyCall.id);
  };

  return (
    <section
      className="Screen-Stack"
      style={{
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        // Fill the main pane so call history can use remaining height.
        minHeight: "calc(100vh - 56px)",
        boxSizing: "border-box",
      }}
    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        gap: 16, flexWrap: "wrap", marginBottom: 14, flexShrink: 0,
      }}>
        <div>
          <section className="Page-Heading" style={{ marginBottom: 4 }}>
            <div>
              <span>Sales</span>
              <h1>RevOps V2</h1>
            </div>
          </section>
          <p style={{ margin: 0, ...muted, fontSize: 13.5 }}>
            Basics floor first. One person — what to fix next, with proof.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {isManager && people.length > 0 && (
            <PersonPicker people={people} selectedId={employeeId} onSelect={setEmployeeId} />
          )}
        </div>
      </div>

      {loading && <p style={muted}>Loading…</p>}

      {!loading && isManager && people.length === 0 && (
        <div style={card}>
          <p style={{ ...muted, fontSize: 13, margin: 0 }}>
            Nobody is on a RevOps track yet. People appear once they have an onboarding row.
          </p>
        </div>
      )}

      {!loading && person && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.15fr) minmax(280px, 0.85fr)",
          gap: 14,
          flex: 1,
          minHeight: 0,
          alignItems: "stretch",
        }}>
          <div style={{ minWidth: 0, overflowY: "auto", paddingRight: 2 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap",
            }}>
              <Target size={15} color="var(--blue-600)" />
              <strong style={{ fontSize: 16, color: "var(--c-900)" }}>
                {isManager ? person.name : "Your focus"}
              </strong>
              <button
                type="button"
                onClick={readyCall ? openReady : undefined}
                title={readyCall ? "Newest scored call is ready" : "Nothing scored to check yet"}
                style={{
                  marginLeft: "auto", border: "none", background: "transparent", padding: 0,
                  cursor: readyCall ? "pointer" : "default",
                  display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 11, fontWeight: 400,
                  color: readyCall ? "var(--c-500)" : "var(--c-400)",
                }}
              >
                <CheckCircle2 size={12} />
                {readyCall ? "Ready" : "Not checked"}
              </button>
            </div>

            <BasicsFloor
              calls={personCalls}
              detailsById={detailsById}
              onOpenCall={setSelectedCallId}
            />

            <FocusSection icon={<Crosshair size={13} />} title="Repeated basics issues">
              {patternsError && <div className="Login-Error">{patternsError}</div>}
              {!patterns && !patternsError && <p style={{ margin: 0, fontSize: 13, ...muted }}>Loading…</p>}
              {patterns && patterns.calls_in_window === 0 && (
                <p style={{ margin: 0, fontSize: 13, ...muted }}>
                  No scored calls in the last {patterns.window_days} days — nothing to pattern yet.
                </p>
              )}
              {patterns && patterns.calls_in_window > 0 && repeating.length === 0 && !drill && (
                <p style={{ margin: 0, fontSize: 13, ...muted }}>
                  No repeating basics misses in this window.
                </p>
              )}
              {patterns && (drill || repeating.length > 0) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {drill && (
                    <div style={{
                      padding: "10px 12px", borderRadius: 10, border: "1px solid var(--blue-600)",
                      background: "rgba(80,120,255,.07)",
                    }}>
                      <div style={{
                        fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em",
                        textTransform: "uppercase", color: "var(--blue-600)", marginBottom: 2,
                      }}>
                        Work on this next
                      </div>
                      <strong style={{ fontSize: 14.5, color: "var(--c-900)" }}>{drill.label}</strong>
                      <div style={{ fontSize: 12, ...muted, marginTop: 2 }}>
                        missed on {drill.misses} of {plural(drill.opportunities, "call")}
                        {drill.status_note ? ` · ${drill.status_note}` : ""}
                        {" · "}scored over recent calls
                      </div>
                    </div>
                  )}
                  {repeating.filter((m) => m !== drill).slice(0, 4).map((m) => (
                    <div
                      key={m.parameter_key}
                      style={{
                        display: "flex", justifyContent: "space-between", gap: 10,
                        padding: "6px 0", borderTop: "1px solid var(--c-200)", fontSize: 13,
                      }}
                    >
                      <span style={{ color: "var(--c-900)", fontWeight: 600 }}>{m.label}</span>
                      <span style={{ ...muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {m.misses}/{m.opportunities}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </FocusSection>

            <FocusSection icon={<Wrench size={13} />} title="How to correct">
              {!drill && (
                <p style={{ margin: 0, fontSize: 13, ...muted }}>
                  Pick up a repeating issue first — correction tips land here with the drill.
                </p>
              )}
              {drill && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {drill.ten && (
                    <div>
                      <div style={{
                        fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em",
                      }}>
                        What a 10 sounds like
                      </div>
                      <p style={{ margin: "3px 0 0", fontSize: 13.5, color: "var(--c-800)", lineHeight: 1.5 }}>
                        {drill.ten}
                      </p>
                    </div>
                  )}
                  {(drill.examples || [])[0]?.try_next && (
                    <div>
                      <div style={{
                        fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em",
                      }}>
                        Try next time
                      </div>
                      <p style={{ margin: "3px 0 0", fontSize: 13.5, color: "var(--c-900)", lineHeight: 1.5 }}>
                        → {drill.examples[0].try_next}
                      </p>
                    </div>
                  )}
                  {(drill.examples || [])[0]?.why && (
                    <div>
                      <div style={{
                        fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em",
                      }}>
                        Why it scored low
                      </div>
                      <p style={{ margin: "3px 0 0", fontSize: 13, color: "var(--c-700)", lineHeight: 1.5 }}>
                        {drill.examples[0].why}
                      </p>
                    </div>
                  )}
                  {!drill.ten && !(drill.examples || [])[0]?.try_next && (
                    <p style={{ margin: 0, fontSize: 13, ...muted }}>
                      Open the call analysis for the moment and the line to try.
                    </p>
                  )}
                  {(drill.examples || [])[0]?.call_score_id && (
                    <button
                      type="button"
                      onClick={() => setSelectedCallId(drill.examples[0].call_score_id)}
                      style={{
                        alignSelf: "flex-start", border: "1px solid var(--c-200)", borderRadius: 8,
                        padding: "6px 10px", cursor: "pointer", background: "transparent",
                        fontSize: 12, fontWeight: 600, color: "var(--blue-600)",
                      }}
                    >
                      Hear the moment on a real call
                    </button>
                  )}
                </div>
              )}
            </FocusSection>

            {listed && (
              <div style={{ ...card, marginTop: 4 }}>
                <div style={{
                  fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                  color: "var(--c-600)", marginBottom: 10,
                }}>
                  Open call
                </div>
                {detailLoading && !openCall?.parameters && (
                  <p style={{ ...muted, fontSize: 13, margin: 0 }}>Loading the call…</p>
                )}
                {callDetail?._failed && (
                  <div className="Login-Error">Couldn&apos;t load this call&apos;s details.</div>
                )}
                {openCall && !callDetail?._failed && (
                  <CallCard
                    call={openCall}
                    history={personCalls}
                    onOpenCall={setSelectedCallId}
                    playbookLinks={rubric?.playbook_links || {}}
                    canAdjust={isManager}
                    canDispute={!isManager}
                  />
                )}
              </div>
            )}
          </div>

          <div style={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
            <CallHistoryPanel
              calls={personCalls}
              openId={selectedCallId}
              onSelect={setSelectedCallId}
            />
          </div>
        </div>
      )}
    </section>
  );
}
